/* eslint-disable no-restricted-globals */
/* eslint-disable prefer-let/prefer-let */
/* eslint-disable fp/no-mutating-methods */
/* eslint-disable fp/no-mutation */
import { ReactElement, useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { uniq } from 'ramda'

import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import Divider from '@mui/material/Divider'
import FormControl from '@mui/material/FormControl'
import IconButton from '@mui/material/IconButton'
import InputLabel from '@mui/material/InputLabel'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import MenuItem from '@mui/material/MenuItem'
import Pagination from '@mui/material/Pagination'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Snackbar from '@mui/material/Snackbar'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import {
  DeleteIcon,
  DownloadIcon,
  ExpandIcon,
  InfoIcon,
  LaunchIcon,
  ViewStatsIcon,
} from 'src/v3/core/icons'
import { UserExtra as User } from 'src/user/user.types'
import { UserGroup } from 'src/v3/manage/manage.slice'
import { TrainingSeries } from 'src/v3/core/types'
import CourseCard, { CourseData } from 'src/components/common/course-card'
import { downloadFile } from 'src/v3/utilities/filesystem'
import ConfirmAction from 'src/v3/components/confirm-action'
import UserCard from './user-card'

type BaseEntity = {
  id: string
  name: string
}

interface Props<T extends BaseEntity> {
  entities: T[]
  route?: string
  onDelete?: (id: string) => Promise<unknown>
  customAction?: React.FunctionComponent<{ entity: T }>
  customActionExtra?: number
  info?: {
    card: React.FunctionComponent<{ entity: T; onCancel: () => void }>
    stats?: boolean
  }
  checkbox?: {
    onClick: (id: string[]) => void
    checked: string[]
  }
  filters?: {
    filter: (entity: T) => boolean
    list: {
      label: string
      value: string | number
      setValue: (value: string) => void
      options?: BaseEntity[]
    }[]
    excludeName?: boolean
  }
}

type UserListProps = Pick<Props<User>, 'route' | 'onDelete' | 'checkbox'> & {
  users: User[]
  groups?: UserGroup[]
}

type CourseListProps = Pick<Props<User>, 'route' | 'onDelete' | 'checkbox'> & {
  courses: CourseData[]
  trainingSeries?: TrainingSeries[]
  stats?: boolean
}

export function EntityList<T extends BaseEntity>({
  entities,
  route,
  onDelete,
  info,
  customAction,
  customActionExtra,
  checkbox,
  filters,
}: Props<T>): ReactElement {
  let navigate = useNavigate()

  let [page, setPage] = useState<number>(1)
  let [nameFilter, setNameFilter] = useState<string>('')
  let [errorMessage, setErrorMessage] = useState<string>('')
  let [dialogStatus, setDialogStatus] = useState<string>('')

  let filteredEntities = entities
    .filter((entity) =>
      entity.name?.toLowerCase().includes(nameFilter.toLowerCase()),
    )
    .filter(filters ? filters.filter : () => true)

  useEffect(() => {
    if (filteredEntities.length <= (page - 1) * 10) {
      setPage(page - 1 || 1)
    }
  }, [filteredEntities])

  let handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value)
  }

  let handleCheckboxToggle = (value: string) => {
    let { checked } = checkbox!
    let currentIndex = checked.indexOf(value)
    let newChecked = [...checked]

    if (currentIndex === -1) {
      newChecked.push(value)
    } else {
      newChecked.splice(currentIndex, 1)
    }

    return newChecked
  }

  let selectAll = (checked: string[], filtered: T[]): string[] => {
    let checkedFiltered = checked.filter((c) =>
      filtered.some((f) => f.id === c),
    )

    if (filtered.length === checkedFiltered.length) {
      return checked.filter((c) => !filtered.some((f) => f.id === c))
    }

    return uniq([...checked, ...filtered.map((f) => f.id)])
  }

  let [errTitle, errMsg] = errorMessage.split(':')
  let buttonCount = [onDelete, route, customAction, info && !route].reduce(
    (sum, icon) => sum + (icon ? 1 : 0),
    0,
  )

  if (info?.stats && customActionExtra) {
    buttonCount += 1
  }

  return (
    <Paper variant="outlined" elevation={0} className="mt-2 mb-4">
      <Accordion disableGutters>
        <AccordionSummary expandIcon={<ExpandIcon />}>Filters</AccordionSummary>
        <AccordionDetails
          className="flex px-3 pt-0 justify-content-between"
          sx={{
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <>
            {!filters?.excludeName && (
              <TextField
                variant="outlined"
                label="Name"
                name="name"
                type="text"
                size="small"
                value={nameFilter}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setNameFilter(event.target.value)
                }}
                sx={{ flexBasis: 150, flexGrow: 1, flexShrink: 1 }}
              />
            )}
            {filters?.list.map((property) =>
              !property.options ? (
                <TextField
                  key={property.label}
                  value={property.value}
                  label={property.label}
                  name={property.label.toLowerCase()}
                  variant="outlined"
                  size="small"
                  type="text"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    property.setValue(event.target.value)
                  }
                  sx={{ flexBasis: 150, flexGrow: 1, flexShrink: 1 }}
                />
              ) : (
                <FormControl
                  key={property.label}
                  sx={{ flexBasis: 150, flexGrow: 1, flexShrink: 1 }}
                >
                  <InputLabel size="small" sx={{ paddingRight: 2 }}>
                    {property.label}
                  </InputLabel>
                  <Select
                    value={property.value}
                    label={property.label}
                    variant="outlined"
                    size="small"
                    onChange={(event) =>
                      property.setValue(`${event.target.value}`)
                    }
                  >
                    <MenuItem key={'none'} value={''}>
                      None
                    </MenuItem>
                    {property.options.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ),
            )}
          </>
        </AccordionDetails>
      </Accordion>
      {checkbox && (
        <ListItem dense divider>
          <ListItemIcon>
            <Tooltip title="Select All" placement="top">
              <Checkbox
                edge="start"
                checked={filteredEntities.every(
                  (item) => checkbox.checked.indexOf(item.id) >= 0,
                )}
                tabIndex={-1}
                disableRipple
                onClick={() =>
                  checkbox.onClick(
                    selectAll(checkbox.checked, filteredEntities),
                  )
                }
              />
            </Tooltip>
          </ListItemIcon>
          <ListItemText id={'header'} primary="Name" />
        </ListItem>
      )}
      <List
        sx={{
          overflow: 'auto',
          width: '100%',
          backgroundColor: 'background.paper',
          minHeight: 195,
          height: entities.length >= 10 ? 490 : entities.length * 49,
        }}
        disablePadding
      >
        {filteredEntities.slice(10 * (page - 1), 10 * page).map((entity) => (
          <ListItem
            key={entity.id}
            secondaryAction={
              <>
                {customAction && customAction({ entity })}
                {route && (
                  <Tooltip title="View">
                    <IconButton
                      aria-label="view"
                      onClick={() => navigate(`${route}/${entity.id}`)}
                    >
                      <LaunchIcon />
                    </IconButton>
                  </Tooltip>
                )}
                {info && !route && (
                  <>
                    <Tooltip title={info.stats ? 'Stats' : 'Info'}>
                      <IconButton
                        onClick={() => setDialogStatus(`info-${entity.id}`)}
                      >
                        {info.stats ? <ViewStatsIcon /> : <InfoIcon />}
                      </IconButton>
                    </Tooltip>
                    <Dialog
                      open={dialogStatus === `info-${entity.id}`}
                      onClose={() => setDialogStatus('')}
                      scroll="paper"
                      fullWidth={true}
                      maxWidth="xs"
                    >
                      {
                        info.card({
                          entity,
                          onCancel: () => setDialogStatus(''),
                        }) as React.ReactNode
                      }
                    </Dialog>
                  </>
                )}
                {onDelete && (
                  <>
                    <Tooltip title="Delete">
                      <IconButton
                        aria-label="delete"
                        onClick={() => {
                          setDialogStatus(`delete-${entity.id}`)
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                    <ConfirmAction
                      executeAction={() => onDelete(entity.id)}
                      open={dialogStatus}
                      setOpen={setDialogStatus}
                      openText={`delete-${entity.id}`}
                      title="Confirm Deletion"
                      text={`Are you sure you want to delete ${entity.name}?`}
                      onFailure={(err) =>
                        setErrorMessage(`Error Deleting ${entity.name}: ${err}`)
                      }
                    />
                  </>
                )}
              </>
            }
            sx={{ pr: 6 * buttonCount }}
            disablePadding={!!checkbox}
            divider
          >
            {!checkbox ? (
              <ListItemText primary={entity.name} />
            ) : (
              <ListItemButton
                onClick={() =>
                  checkbox.onClick(handleCheckboxToggle(entity.id))
                }
                dense
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={checkbox?.checked.indexOf(entity.id!) !== -1}
                    tabIndex={-1}
                    disableRipple
                    sx={{ paddingY: 1 }}
                  />
                </ListItemIcon>
                <ListItemText primary={entity.name} />
              </ListItemButton>
            )}
          </ListItem>
        ))}
      </List>
      <Divider />
      <Pagination
        size="small"
        sx={{ paddingY: '5px', display: 'flex', justifyContent: 'center' }}
        count={Math.ceil(filteredEntities.length / 10)}
        page={page}
        onChange={handlePageChange}
        showFirstButton
        showLastButton
      />
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          <strong>{errTitle}:</strong>
          {errMsg}
        </Alert>
      </Snackbar>
    </Paper>
  )
}

export default EntityList

export function CourseList({
  courses,
  trainingSeries,
  route,
  onDelete,
  checkbox,
  stats,
}: CourseListProps): ReactElement {
  let [trainingSeriesFilter, setTrainingSeriesFilter] = useState<string>('')
  let [sectionFilter, setSectionFilter] = useState<string>('')

  let filterList = [
    {
      label: 'Section',
      value: sectionFilter,
      setValue: setSectionFilter,
      options: uniq(
        courses.map((c) => ({ id: c.sectionID, name: c.section || '' })),
      ).filter((s) => !!s.name),
    },
  ]
  if (trainingSeries) {
    filterList.push({
      label: 'Training Series',
      value: trainingSeriesFilter,
      setValue: setTrainingSeriesFilter,
      options: trainingSeries,
    })
  }

  const overlayStyle = {
    top: 0,
    left: 0,
    bottom: 0,
    right: 0,
    position: 'absolute',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  return (
    <EntityList
      entities={courses}
      route={route}
      onDelete={onDelete}
      info={{ card: CourseCard, stats }}
      customAction={
        !stats
          ? undefined
          : ({ entity }): ReactElement => {
              const totalUsers = entity.assignmentData
                ? entity.assignmentData.completedUsers.length +
                  entity.assignmentData.pendingUsers.length
                : 0
              const percentage = entity.assignmentData
                ? Math.round(
                    (100 * entity.assignmentData.completedUsers.length) /
                      (entity.assignmentData.completedUsers.length +
                        entity.assignmentData.pendingUsers.length),
                  )
                : NaN

              return (
                <>
                  <Tooltip
                    title={`${entity.assignmentData?.completedUsers.length || 0} / ${totalUsers} Users Complete`}
                    placement="left"
                  >
                    <span>
                      <IconButton disabled>
                        <Box sx={{ position: 'relative', display: 'flex' }}>
                          <CircularProgress
                            color="inherit"
                            variant="determinate"
                            value={100}
                            size={36}
                          />
                          <Box sx={overlayStyle}>
                            <CircularProgress
                              color={percentage === 100 ? 'success' : 'primary'}
                              variant="determinate"
                              value={isNaN(percentage) ? 0 : percentage}
                              size={36}
                            />
                          </Box>
                          <Box sx={overlayStyle}>
                            <Typography
                              color="text.primary"
                              sx={{ fontSize: 11 }}
                            >
                              {isNaN(percentage)
                                ? 'N/A'
                                : `${Math.round(percentage)}%`}
                            </Typography>
                          </Box>
                        </Box>
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Download CSV">
                    <IconButton
                      onClick={() => {
                        downloadFile(
                          [
                            'Name,Status',
                            ...(entity.assignmentData?.pendingUsers || []).map(
                              (x) => `${x},Pending`,
                            ),
                            ...(
                              entity.assignmentData?.completedUsers || []
                            ).map((x) => `${x},Completed`),
                          ].join('\r\n'),
                          `${entity.name}_user_data.csv`,
                        )
                      }}
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                </>
              )
            }
      }
      customActionExtra={stats ? 1 : 0}
      checkbox={checkbox}
      filters={{
        filter: (courseData) =>
          (!sectionFilter || courseData.sectionID === sectionFilter) &&
          (!trainingSeries ||
            (trainingSeries
              .find((ts) => ts.id === trainingSeriesFilter)
              ?.courseIDs?.indexOf(courseData.id!) ?? 1) > -1),
        list: filterList,
      }}
    />
  )
}

export function UserList({
  users,
  groups,
  route,
  onDelete,
  checkbox,
}: UserListProps): ReactElement {
  let [groupFilter, setGroupFilter] = useState<string>('')

  return (
    <EntityList
      entities={users}
      info={{ card: UserCard }}
      route={route}
      onDelete={onDelete}
      filters={
        groups
          ? {
              filter: (user) =>
                (groups
                  .find((g) => g.id === groupFilter)
                  ?.users?.indexOf(user.id!) ?? 1) > -1,
              list: [
                {
                  label: 'Group',
                  value: groupFilter,
                  setValue: setGroupFilter,
                  options: groups as any[],
                },
              ],
            }
          : undefined
      }
      checkbox={checkbox}
    />
  )
}

export function SeriesClassList<T extends BaseEntity>({
  entities,
  route,
  onDelete,
  info,
  customAction,
  checkbox,
  filters,
  episodeAssignments,
  setEpisodeAssignments,
  updateSeriesCourse,
}: Props<T> & {
  episodeAssignments: { courseID: string; episode: number; season: number }[]
  setEpisodeAssignments: (
    x: { courseID: string; episode: number; season: number }[],
  ) => void
  updateSeriesCourse: (
    courseID: string,
    season: number,
    episode: number,
  ) => void
}): ReactElement {
  let navigate = useNavigate()

  let [page, setPage] = useState<number>(1)
  let [nameFilter, setNameFilter] = useState<string>('')
  let [errorMessage, setErrorMessage] = useState<string>('')
  let [dialogStatus, setDialogStatus] = useState<string>('')

  let filteredEntities = entities
    .filter((entity) =>
      entity.name?.toLowerCase().includes(nameFilter.toLowerCase()),
    )
    .filter(filters ? filters.filter : () => true)

  useEffect(() => {
    if (filteredEntities.length <= (page - 1) * 10) {
      setPage(page - 1 || 1)
    }
  }, [filteredEntities])

  let handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value)
  }

  let handleCheckboxToggle = (value: string) => {
    let { checked } = checkbox!
    let currentIndex = checked.indexOf(value)
    let newChecked = [...checked]

    if (currentIndex === -1) {
      newChecked.push(value)
    } else {
      newChecked.splice(currentIndex, 1)
    }

    return newChecked
  }

  let selectAll = (checked: string[], filtered: T[]): string[] => {
    let checkedFiltered = checked.filter((c) =>
      filtered.some((f) => f.id === c),
    )

    if (filtered.length === checkedFiltered.length) {
      return checked.filter((c) => !filtered.some((f) => f.id === c))
    }

    return uniq([...checked, ...filtered.map((f) => f.id)])
  }

  let [errTitle, errMsg] = errorMessage.split(':')
  let buttonCount = [onDelete, route, customAction, info && !route].reduce(
    (sum, icon) => sum + (icon ? 1 : 0),
    0,
  )

  return (
    <Paper variant="outlined" elevation={0} className="mt-2 mb-4">
      <Accordion disableGutters>
        <AccordionSummary expandIcon={<ExpandIcon />}>Filters</AccordionSummary>
        <AccordionDetails
          className="flex px-3 pt-0 justify-content-between"
          sx={{
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <>
            {!filters?.excludeName && (
              <TextField
                variant="outlined"
                label="Name"
                name="name"
                type="text"
                size="small"
                value={nameFilter}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setNameFilter(event.target.value)
                }}
                sx={{ flexBasis: 150, flexGrow: 1, flexShrink: 1 }}
              />
            )}
            {filters?.list.map((property) =>
              !property.options ? (
                <TextField
                  key={property.label}
                  value={property.value}
                  label={property.label}
                  name={property.label.toLowerCase()}
                  variant="outlined"
                  size="small"
                  type="text"
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    property.setValue(event.target.value)
                  }
                  sx={{ flexBasis: 150, flexGrow: 1, flexShrink: 1 }}
                />
              ) : (
                <FormControl
                  key={property.label}
                  sx={{ flexBasis: 150, flexGrow: 1, flexShrink: 1 }}
                >
                  <InputLabel size="small" sx={{ paddingRight: 2 }}>
                    {property.label}
                  </InputLabel>
                  <Select
                    value={property.value}
                    label={property.label}
                    variant="outlined"
                    size="small"
                    onChange={(event) =>
                      property.setValue(`${event.target.value}`)
                    }
                  >
                    <MenuItem key={'none'} value={''}>
                      None
                    </MenuItem>
                    {property.options.map((option) => (
                      <MenuItem key={option.id} value={option.id}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ),
            )}
          </>
        </AccordionDetails>
      </Accordion>
      {checkbox && (
        <ListItem dense divider>
          <ListItemIcon>
            <Tooltip title="Select All" placement="top">
              <Checkbox
                edge="start"
                checked={filteredEntities.every(
                  (item) => checkbox.checked.indexOf(item.id) >= 0,
                )}
                tabIndex={-1}
                disableRipple
                onClick={() =>
                  checkbox.onClick(
                    selectAll(checkbox.checked, filteredEntities),
                  )
                }
              />
            </Tooltip>
          </ListItemIcon>
          <ListItemText id={'header'} primary="Name" />
        </ListItem>
      )}
      <List
        sx={{
          overflow: 'auto',
          width: '100%',
          backgroundColor: 'background.paper',
          minHeight: 195,
          height: entities.length >= 10 ? 490 : entities.length * 49,
          padding: 2,
        }}
        disablePadding
      >
        {filteredEntities.slice(10 * (page - 1), 10 * page).map((entity) => (
          <ListItem
            key={entity.id}
            secondaryAction={
              <>
                {customAction && customAction({ entity })}
                {route && (
                  <Tooltip title="View">
                    <IconButton
                      aria-label="view"
                      onClick={() => navigate(`${route}/${entity.id}`)}
                    >
                      <LaunchIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <div className="flex" style={{ width: 100 }}>
                  <TextField
                    value={
                      episodeAssignments.find((x) => x.courseID === entity.id)
                        ?.season ?? ''
                    }
                    label={'S'}
                    name={'season'}
                    variant="outlined"
                    size="small"
                    type="text"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      let seriesCourse = episodeAssignments.find(
                        (x) => x.courseID === entity.id,
                      )
                      setEpisodeAssignments(
                        episodeAssignments.map((x) =>
                          x.courseID === entity.id
                            ? { ...x, season: event.target.value }
                            : x,
                        ) as any,
                      )
                      updateSeriesCourse(
                        entity.id,
                        seriesCourse?.episode ?? 0,
                        event.target.value as any,
                      )
                    }}
                    sx={{ flexBasis: 150, flexGrow: 1, flexShrink: 1 }}
                  />
                  <TextField
                    value={
                      episodeAssignments.find((x) => x.courseID === entity.id)
                        ?.episode ?? ''
                    }
                    label={'E'}
                    name={'episode'}
                    variant="outlined"
                    size="small"
                    type="text"
                    className="ml-2"
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      let seriesCourse = episodeAssignments.find(
                        (x) => x.courseID === entity.id,
                      )
                      setEpisodeAssignments(
                        episodeAssignments.map((x) =>
                          x.courseID === entity.id
                            ? { ...x, episode: event.target.value }
                            : x,
                        ) as any,
                      )
                      updateSeriesCourse(
                        entity.id,
                        event.target.value as any,
                        seriesCourse?.season ?? 0,
                      )
                    }}
                    sx={{ flexBasis: 150, flexGrow: 1, flexShrink: 1 }}
                  />
                </div>
                {onDelete && (
                  <>
                    <Tooltip title="Delete">
                      <IconButton
                        aria-label="delete"
                        onClick={() => {
                          setDialogStatus(`delete-${entity.id}`)
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                    <ConfirmAction
                      executeAction={() => onDelete(entity.id)}
                      open={dialogStatus}
                      setOpen={setDialogStatus}
                      openText={`delete-${entity.id}`}
                      title="Confirm Deletion"
                      text={`Are you sure you want to delete ${entity.name}?`}
                      onFailure={(err) =>
                        setErrorMessage(`Error Deleting ${entity.name}: ${err}`)
                      }
                    />
                  </>
                )}
              </>
            }
            sx={{ pr: 6 * buttonCount }}
            disablePadding={!!checkbox}
            divider
          >
            {!checkbox ? (
              <ListItemText primary={entity.name} />
            ) : (
              <ListItemButton
                onClick={() =>
                  checkbox.onClick(handleCheckboxToggle(entity.id))
                }
                dense
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={checkbox?.checked.indexOf(entity.id!) !== -1}
                    tabIndex={-1}
                    disableRipple
                    sx={{ paddingY: 1 }}
                  />
                </ListItemIcon>
                <ListItemText primary={entity.name} />
              </ListItemButton>
            )}
          </ListItem>
        ))}
      </List>
      <Divider />
      <Pagination
        size="small"
        sx={{ paddingY: '5px', display: 'flex', justifyContent: 'center' }}
        count={Math.ceil(filteredEntities.length / 10)}
        page={page}
        onChange={handlePageChange}
        showFirstButton
        showLastButton
      />
      <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
        <Alert
          variant="filled"
          onClose={() => setErrorMessage('')}
          severity="error"
        >
          <strong>{errTitle}:</strong>
          {errMsg}
        </Alert>
      </Snackbar>
    </Paper>
  )
}
